import type { WidgetInstance } from "@kourai/api/home-board";
import type { ComponentType } from "react";
import type { z } from "zod";

/**
 * The widget API (ADMIN-257): what a widget declares, and what it gets from the board.
 *
 * Every widget, ours included, is a `defineWidget(...)` manifest plus a lazily loaded React
 * component. The board only reads the manifest (sizes, surfaces, permissions, config schema); it
 * never knows what a widget does. The component gets its config, its size and a small `host` API,
 * nothing else, so a widget can't reach into the app.
 *
 * This file and `registry.ts` are meant to become the open source widgets SDK (ADMIN-271): keep
 * them free of app imports (auth, routes, assistant-ui).
 */

/**
 * The preset sizes, in grid cells (the grid has 8 columns): S, M, L and W from the plan, plus XL
 * (full width, 4 rows), which is today's Recent list and the default board's only widget.
 */
export const WIDGET_SIZES = {
  s: { w: 2, h: 2 },
  m: { w: 4, h: 2 },
  l: { w: 4, h: 4 },
  w: { w: 8, h: 2 },
  xl: { w: 8, h: 4 },
} as const;
export type WidgetSize = keyof typeof WIDGET_SIZES;

/** Where a widget can appear. The side panel shows the board as one column (ADMIN-265). */
export type WidgetSurface = "newtab" | "sidepanel";

/**
 * Whose data a widget keeps: the person's own, or the whole workspace's (team widgets).
 *
 * @public widget SDK (ADMIN-271)
 */
export type WidgetDataScope = "none" | "member" | "workspace";

/**
 * Browser APIs a widget may ask for. Only these keys of `host.browser` are set for it; anything
 * it didn't declare is simply absent. Optional permissions (topSites, history, bookmarks) are
 * requested the first time the widget is added (ADMIN-262).
 *
 * @public widget SDK (ADMIN-271)
 */
export const WIDGET_PERMISSIONS = ["tabs", "topSites", "history", "bookmarks"] as const;
export type WidgetPermission = (typeof WIDGET_PERMISSIONS)[number];

/**
 * Permissions the extension doesn't hold until someone agrees: a widget asks with
 * `host.requestPermission` (from a click), and its `host.browser` key appears once granted.
 */
export const OPTIONAL_PERMISSIONS: readonly WidgetPermission[] = [
  "topSites",
  "history",
  "bookmarks",
];

/** The subset of the WebExtension API that permissions unlock, typed loosely on purpose. */
export type WidgetBrowser = Partial<Record<WidgetPermission, unknown>>;

/** What the board hands every widget. */
export type WidgetHost<Config> = {
  /** This widget instance's id on the board, stable for its life. */
  widgetId: string;
  /** The workspace whose board this is, and the signed-in person. */
  workspace: { id: string; name: string };
  user: { id: string; name: string };
  /**
   * This widget's own stored content (notes text, a task list), kept per workspace and per the
   * widget's `data` scope. `initial` is used until something is saved.
   */
  useData: <T>(initial: T) => [T, (next: T | ((prev: T) => T)) => void];
  /** Replace part of this widget's config; checked against its schema, then saved with the board. */
  setConfig: (patch: Partial<Config>) => void;
  /** Start a chat with this prompt (in the new tab's composer). */
  ask: (prompt: string) => void;
  /** Open a URL: in this tab, or a new one. */
  openUrl: (url: string, options?: { newTab?: boolean }) => void;
  /**
   * Only the browser APIs this widget declared in `permissions`, and of those only the ones
   * granted: an optional one is absent until `requestPermission` succeeds. On the web (no
   * extension) it's empty.
   */
  browser: WidgetBrowser;
  /**
   * Asks for one of this widget's declared permissions. Call it from a click: the browser shows
   * its prompt the first time. Resolves true once granted (at once if it already was).
   */
  requestPermission: (permission: WidgetPermission) => Promise<boolean>;
  /** True while the board is in Customize mode, so a widget can hide its own controls. */
  editing: boolean;
};

export type WidgetProps<Config = Record<string, unknown>> = {
  /** The instance's config, already parsed with defaults filled in. */
  config: Config;
  size: WidgetSize | "custom";
  /** The tile's size in cells, which is what to lay out against. */
  cells: { w: number; h: number };
  host: WidgetHost<Config>;
};

/** @public widget SDK (ADMIN-271) */
export type WidgetComponent<Config> = ComponentType<WidgetProps<Config>>;

export type WidgetDefinition<Config = Record<string, unknown>> = {
  /** A dotted, lowercase name, unique across all widgets: "kourai.notes". */
  type: string;
  /** The config format's version. Bump it and add `migrate` when the config shape changes. */
  version: number;
  title: string;
  description: string;
  /** Allowed sizes, largest last is a good habit. The first one is used if `defaultSize` is unset. */
  sizes: readonly WidgetSize[];
  defaultSize?: WidgetSize;
  surfaces: readonly WidgetSurface[];
  data: WidgetDataScope;
  permissions: readonly WidgetPermission[];
  /** Parses an instance's config, filling defaults (use `.default()` on every field). */
  config: z.ZodType<Config>;
  /**
   * Turns a config saved by an older `version` into the current shape, before it's parsed.
   * Called with the stored config and the version it was saved with.
   */
  migrate?: (config: Record<string, unknown>, from: number) => Record<string, unknown>;
  /**
   * Built in widgets that the board places itself: the command box. Hidden from the add tray and
   * can't be removed, moved or resized.
   */
  pinned?: boolean;
  /**
   * False for widgets the app creates itself (a pinned answer, from "Pin to home"): they're never
   * in the add tray. Defaults to true.
   */
  addable?: boolean;
  /** At most this many on one board (1 for the command box, Recent, the clock…). */
  max?: number;
  /** The component, code split so only widgets on the board are downloaded. */
  load: () => Promise<{ default: WidgetComponent<Config> }>;
};

const TYPE_RE = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/;

/**
 * Declares a widget. Checks the manifest once, when the module loads, so a mistake fails loudly in
 * development and tests instead of quietly breaking someone's board.
 */
export function defineWidget<Config>(def: WidgetDefinition<Config>): WidgetDefinition<Config> {
  if (!TYPE_RE.test(def.type)) throw new Error(`widget type "${def.type}" must look like a.b`);
  if (!Number.isInteger(def.version) || def.version < 1)
    throw new Error(`widget ${def.type}: version must be a whole number from 1`);
  if (def.sizes.length === 0) throw new Error(`widget ${def.type}: list at least one size`);
  if (def.defaultSize && !def.sizes.includes(def.defaultSize))
    throw new Error(`widget ${def.type}: defaultSize must be one of its sizes`);
  if (def.surfaces.length === 0) throw new Error(`widget ${def.type}: list at least one surface`);
  for (const p of def.permissions)
    if (!WIDGET_PERMISSIONS.includes(p))
      throw new Error(`widget ${def.type}: unknown permission ${p}`);
  return def;
}

/** The size an instance's cells match, or "custom" when it was resized by keyboard. */
export function sizeOf(item: Pick<WidgetInstance, "w" | "h">): WidgetSize | "custom" {
  for (const [name, cells] of Object.entries(WIDGET_SIZES))
    if (cells.w === item.w && cells.h === item.h) return name as WidgetSize;
  return "custom";
}
